import type { Conversation, ConversationParticipantRef, Message } from '../types/Conversation';
import {
  INTERACTIVE_BUTTON_DEFAULT_MAX,
  resolveInteractiveButtonMaxCount,
  type InteractiveButtonChannel,
} from '../../../shared/utils/interactiveButtonLimits';

const isWebchatParticipant = (participant: ConversationParticipantRef): boolean => {
  const label = participant.contactChannelLabel?.trim().toLowerCase();
  const value = participant.contactChannelValue?.trim().toLowerCase() ?? '';
  return label === 'webchat' || value.startsWith('webchat:');
};

const inferChannelsFromConversation = (
  conversation: Pick<
    Conversation,
    | 'containsWhatsAppMessages'
    | 'containsInstagramMessages'
    | 'containsInstagramComments'
    | 'containsMessengerComments'
    | 'messages'
    | 'participants'
  >,
): InteractiveButtonChannel[] => {
  const channels = new Set<InteractiveButtonChannel>();

  if (conversation.containsWhatsAppMessages) {
    channels.add('whatsapp');
  }
  if (conversation.containsInstagramMessages || conversation.containsInstagramComments) {
    channels.add('instagram');
  }
  if (conversation.containsMessengerComments) {
    channels.add('messenger');
  }
  if (conversation.participants?.some(isWebchatParticipant)) {
    channels.add('webchat');
  }

  for (const message of conversation.messages ?? []) {
    const source = (message as Message).source?.trim().toLowerCase();
    switch (source) {
      case 'whatsapp':
        channels.add('whatsapp');
        break;
      case 'instagram':
        channels.add('instagram');
        break;
      case 'messenger':
        channels.add('messenger');
        break;
      case 'telegram':
        channels.add('telegram');
        break;
      case 'webchat':
        channels.add('webchat');
        break;
      default:
        break;
    }
  }

  return Array.from(channels);
};

/**
 * Prefer API `interactiveButtonMaxCount` when present; otherwise derive from conversation channels.
 */
export const resolveConversationInteractiveButtonMaxCount = (
  conversation: Conversation | null | undefined,
): number | null => {
  if (!conversation) {
    return INTERACTIVE_BUTTON_DEFAULT_MAX;
  }

  if (conversation.interactiveButtonMaxCount !== undefined) {
    return conversation.interactiveButtonMaxCount;
  }

  return resolveInteractiveButtonMaxCount(inferChannelsFromConversation(conversation));
};
